import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Folder, FileText, CurrencyDollar, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

export function PortalDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, dossiersRes] = await Promise.all([
          api.get('/portal/stats'),
          api.get('/portal/dossiers'),
        ]);
        setStats(statsRes.data);
        setDossiers(dossiersRes.data.items || []);
      } catch {
        toast.error('Erreur de chargement');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  const getStatutColor = (statut) => {
    const map = {
      brouillon: 'bg-gray-100 text-gray-800',
      ouvert: 'bg-blue-100 text-blue-800',
      complet: 'bg-emerald-100 text-emerald-800',
      en_analyse: 'bg-yellow-100 text-yellow-800',
      valide: 'bg-green-100 text-green-800',
      suspendu: 'bg-orange-100 text-orange-800',
      cloture: 'bg-gray-100 text-gray-600',
    };
    return map[statut] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome */}
      <div>
        <h1 className="text-2xl font-bold text-[#1A2421]" style={{ fontFamily: 'Manrope' }} data-testid="portal-welcome-title">
          Bonjour, {user?.name || 'Membre'}
        </h1>
        <p className="text-[#5E6D66] mt-1 text-sm">
          Bienvenue sur votre espace membre FTPE
          {user?.organization?.raison_sociale && ` — ${user.organization.raison_sociale}`}
        </p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-[#E8E6E1]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg"><Folder size={20} className="text-blue-600" /></div>
              <div>
                <p className="text-2xl font-bold text-[#1A2421]" data-testid="portal-stat-dossiers">{stats.dossiers_actifs}</p>
                <p className="text-xs text-[#5E6D66]">Dossiers actifs</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E8E6E1]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg"><FileText size={20} className="text-emerald-600" /></div>
              <div>
                <p className="text-2xl font-bold text-[#1A2421]">{stats.documents_total}</p>
                <p className="text-xs text-[#5E6D66]">Documents</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E8E6E1]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-[#2D5A4C]/10 rounded-lg"><CheckCircle size={20} className="text-[#2D5A4C]" /></div>
              <div>
                <p className="text-2xl font-bold text-[#1A2421]">{stats.checklist_percent}%</p>
                <p className="text-xs text-[#5E6D66]">Checklist</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-[#E8E6E1]">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg"><CurrencyDollar size={20} className="text-orange-600" /></div>
              <div>
                <p className="text-2xl font-bold text-[#1A2421]">{stats.paiements_en_attente}</p>
                <p className="text-xs text-[#5E6D66]">Paiements en attente</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Checklist progress */}
      {stats && stats.checklist_total > 0 && (
        <Card className="border-[#E8E6E1]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-[#1A2421]">Progression globale des documents</p>
              <span className="text-sm text-[#5E6D66]">{stats.checklist_checked}/{stats.checklist_total} fournis</span>
            </div>
            <div className="w-full h-3 bg-[#E8E6E1] rounded-full overflow-hidden">
              <div
                className="h-full bg-[#2D5A4C] rounded-full transition-all duration-500"
                style={{ width: `${stats.checklist_percent}%` }}
                data-testid="portal-checklist-progress"
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dossiers list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-[#1A2421]" style={{ fontFamily: 'Manrope' }}>Mes dossiers</h2>
          <Button variant="ghost" size="sm" onClick={() => navigate('/portail/dossiers')} className="text-[#2D5A4C]">
            Voir tout <ArrowRight size={16} className="ml-1" />
          </Button>
        </div>

        {dossiers.length === 0 ? (
          <Card className="border-[#E8E6E1]">
            <CardContent className="p-8 text-center">
              <Folder size={40} className="mx-auto text-[#E8E6E1] mb-3" />
              <p className="text-[#5E6D66]">Aucun dossier en cours</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {dossiers.slice(0, 5).map((d) => {
              const progress = d.checklist?.length > 0
                ? Math.round((d.checklist.filter(c => c.checked).length / d.checklist.length) * 100)
                : 0;
              return (
                <Card key={d.id} className="border-[#E8E6E1] hover:shadow-sm transition-shadow cursor-pointer" onClick={() => navigate(`/portail/dossiers?id=${d.id}`)} data-testid={`portal-dossier-${d.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-[#2D5A4C]/10 rounded-lg"><Folder size={18} className="text-[#2D5A4C]" /></div>
                        <div>
                          <p className="text-sm font-medium text-[#1A2421]">{d.numero_dossier}</p>
                          <p className="text-xs text-[#5E6D66]">{(d.type_dossier || '').replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {d.checklist?.length > 0 && (
                          <div className="flex items-center gap-2">
                            <div className="w-16 h-1.5 bg-[#E8E6E1] rounded-full overflow-hidden">
                              <div className="h-full bg-[#2D5A4C] rounded-full" style={{ width: `${progress}%` }} />
                            </div>
                            <span className="text-[10px] text-[#5E6D66]">{progress}%</span>
                          </div>
                        )}
                        <Badge className={getStatutColor(d.statut)}>
                          {(d.statut || '').replace(/_/g, ' ')}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
